import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
// import { BannerAd } from '../components/GoogleAd'; // Uncomment when ready
import { User, Mail, Lock, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { userAPI } from '../services/api';
import { sanitizeName, sanitizeEmail } from '../utils/sanitize';
import './Settings.css';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Sanitize inputs based on field type
    let sanitizedValue = value;
    try {
      if (name === 'name') {
        sanitizedValue = sanitizeName(value);
      } else if (name === 'email') {
        sanitizedValue = value.trim(); // Don't sanitize email during typing
      }
    } catch (err) {
      // Invalid input, keep original for user to see error
    }
    
    setFormData({
      ...formData,
      [name]: sanitizedValue
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validation
      if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
        setError('New passwords do not match');
        setLoading(false);
        return;
      }

      if (formData.newPassword && formData.newPassword.length < 6) {
        setError('New password must be at least 6 characters');
        setLoading(false);
        return;
      }

      // Update profile (name/email) if changed
      if (formData.name !== user?.name || formData.email !== user?.email) {
        // Final sanitization before sending to API
        const sanitizedName = sanitizeName(formData.name);
        const sanitizedEmail = sanitizeEmail(formData.email);
        await userAPI.updateProfile(sanitizedName, sanitizedEmail);
      }

      // Change password if provided
      if (formData.currentPassword && formData.newPassword) {
        await userAPI.changePassword(formData.currentPassword, formData.newPassword);
        // Clear password fields after success
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
      
      setSuccess('Settings updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  // TESTING MODE: Show ads to ALL users
  // const isFreeUser = true; // Uncomment when ads are ready

  return (
    <div className="settings-page fade-in">
      {/* 📢 BANNER AD */}
      {/* {isFreeUser && <BannerAd />} */}
      
      <div className="page-header">
        <div>
          <h1>Account Settings</h1>
          <p>Manage your account details and preferences</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          <span>{success}</span>
        </div>
      )}

      <div className="settings-grid">
        {/* Profile Settings */}
        <div className="card">
          <div className="card-header">
            <h2>Profile Information</h2>
            <p>Update your account details</p>
          </div>

          <form onSubmit={handleSubmit} className="settings-form">
            <div className="form-group">
              <label htmlFor="name">
                <User size={18} />
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                <Mail size={18} />
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                disabled={loading}
              />
              <small className="form-help">
                You'll need to verify your new email address
              </small>
            </div>

            <div className="form-divider"></div>

            <h3 className="form-section-title">Change Password</h3>

            <div className="form-group">
              <label htmlFor="currentPassword">
                <Lock size={18} />
                Current Password
              </label>
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">
                <Lock size={18} />
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                <Lock size={18} />
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                disabled={loading}
              />
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="loading"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Account Info */}
        <div className="card">
          <div className="card-header">
            <h2>Account Information</h2>
            <p>Your account details</p>
          </div>

          <div className="info-list">
            <div className="info-item">
              <span className="info-label">Account Status</span>
              <span className="badge badge-success">Active</span>
            </div>
            <div className="info-item">
              <span className="info-label">User ID</span>
              <span className="info-value">{user?.id || 'N/A'}</span>
            </div>
          </div>

          <div className="form-divider"></div>

          <div className="danger-zone">
            <h3>Danger Zone</h3>
            <p>These actions are irreversible. Please be careful.</p>
            <button className="btn btn-outline" style={{ color: 'var(--color-error)', borderColor: 'var(--color-error)' }}>
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;